import { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { normalizeService, normalizePost } from '../services/postService';
import { allWordsPresent } from '../utils/searchUtils';

/**
 * Hook para búsqueda unificada: usuarios, posts y servicios.
 * @param {string} searchTerm - Término de búsqueda (ej. desde ?q=)
 * @returns {{ users: Array, posts: Array, services: Array, loading: boolean, error: string|null }}
 */
export function useSearch(searchTerm) {
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchIdRef = useRef(0);

  useEffect(() => {
    const term = (searchTerm || '').trim();
    if (!term) {
      setUsers([]);
      setPosts([]);
      setServices([]);
      setLoading(false);
      setError(null);
      return;
    }

    const searchId = ++searchIdRef.current;

    const runSearch = async () => {
      setLoading(true);
      setError(null);

      try {
        // Usuarios
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        if (searchId !== searchIdRef.current) return;
        const foundUsers = usersSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(user => {
            const username = (user.username || '').toLowerCase();
            const displayName = (user.displayName || '').toLowerCase();
            const email = (user.email || '').toLowerCase();
            const combined = `${username} ${displayName} ${email}`;
            return allWordsPresent(combined, term);
          })
          .sort((a, b) => {
            const aName = (a.username || a.displayName || '').toLowerCase();
            const bName = (b.username || b.displayName || '').toLowerCase();
            const firstWord = (term.trim().toLowerCase().split(/\s+/)[0] || '');
            const aStarts = aName.startsWith(firstWord);
            const bStarts = bName.startsWith(firstWord);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return 0;
          });

        if (searchId !== searchIdRef.current) return;

        const foundUserIds = new Set(foundUsers.map(u => u.id));

        // Posts: por texto O por autor (usuarios que coincidieron)
        const postsQuery = query(
          collection(db, 'posts'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const postsSnapshot = await getDocs(postsQuery);
        if (searchId !== searchIdRef.current) return;
        const allPostsRaw = postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const allPostsNormalized = allPostsRaw.map(p => normalizePost({ id: p.id, ...p }));
        const postsByText = allPostsNormalized.filter(post => {
          const caption = post.caption || '';
          const userName = post.userName || post.displayName || '';
          const description = post.description || '';
          const combined = `${caption} ${userName} ${description}`;
          return allWordsPresent(combined, term);
        });
        const postsByAuthor = allPostsNormalized.filter(post => {
          const uid = post.userId || post.userID || (post.authorInfo && post.authorInfo.uid);
          return uid && foundUserIds.has(uid);
        });
        const postsById = new Map();
        [...postsByText, ...postsByAuthor].forEach(p => postsById.set(p.id, p));
        const foundPosts = Array.from(postsById.values());

        if (searchId !== searchIdRef.current) return;

        // Servicios: por texto O por autor
        const servicesQuery = query(
          collection(db, 'services'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const servicesSnapshot = await getDocs(servicesQuery);
        if (searchId !== searchIdRef.current) return;
        const allServicesRaw = servicesSnapshot.docs.map(doc => normalizeService({ id: doc.id, ...doc.data() }));
        const servicesByText = allServicesRaw.filter(service => {
          const caption = service.caption || '';
          const userName = service.userName || service.displayName || '';
          const description = service.description || '';
          const category = service.categoryName || service.category || '';
          const combined = `${caption} ${userName} ${description} ${category}`;
          return allWordsPresent(combined, term);
        });
        const servicesByAuthor = allServicesRaw.filter(service => {
          const uid = service.userId || service.userID;
          return uid && foundUserIds.has(uid);
        });
        const servicesById = new Map();
        [...servicesByText, ...servicesByAuthor].forEach(s => servicesById.set(s.id, s));
        const foundServices = Array.from(servicesById.values());

        if (searchId !== searchIdRef.current) return;
        setUsers(foundUsers);
        setPosts(foundPosts);
        setServices(foundServices);
      } catch (err) {
        if (searchId !== searchIdRef.current) return;
        console.error('Error al buscar:', err);
        setError(err.message || 'Error al buscar');
      } finally {
        if (searchId === searchIdRef.current) setLoading(false);
      }
    };

    runSearch();
    return () => {};
  }, [searchTerm]);

  return { users, posts, services, loading, error };
}
